import type { RootState } from '@reducers/index';
import {
  updateCategory,
  updateFilter,
  updateGeo,
  updateQuery,
  updateRecentSearch,
} from '@reducers/search';
import { setPreventPopstate } from '@reducers/system';
import { useViewport } from '@sbt-web/hooks';
import { CategoryID, FilterURI, GeoEntry } from '@sbt-web/network/types';
import {
  Category,
  getAllCategories,
  getCategoryByID,
} from '@sbt-web/category-utils';
import {
  SearchAutocomplete,
  type RecentSearchOption,
  type SearchAutocompleteOption,
} from '@sbt-web/search-autocomplete';
import classnames from 'classnames';
import React, { useCallback } from 'react';
import { batch, connect } from 'react-redux';
import { CategorySelector } from '../Filters/CategorySelector';
import { QueryGeoInput } from '../Filters/QueryGeoInput';
import NationwideSearchExpander from '../SearchNationwide';

import '@sbt-web/search-autocomplete/dist/index.css';
import ownClasses from './search-bar.module.scss';

interface CategoryInfo {
  key: CategoryID;
  label: string;
}
interface OwnProps {
  onFiltersOpen: () => void;
}

interface DispatchProps {
  updateSearch: (
    query?: string,
    category?: Category,
    clothingType?: string,
    geo?: GeoEntry
  ) => void;
  setPreventPopstate: (open: boolean) => void;
  updateRecentSearch: (state: RecentSearchOption) => void;
}

interface StateProps {
  query: string;
  category: Category;
}

export type Props = OwnProps & StateProps & DispatchProps;

export interface State {
  categoryId: CategoryID;
  categoryIndex: number;
}

const categoryList = getAllCategories();

function getCategoryIndex(categoryId: CategoryID): number {
  return categoryList.findIndex((c) => c.id === categoryId);
}

export function SearchBar({
  category,
  onFiltersOpen,
  query = '',
  updateSearch,
  setPreventPopstate,
  updateRecentSearch,
}: Props) {
  const { isMobile } = useViewport();

  const categoryIndex = getCategoryIndex(category.id);
  const onCategoryChange = (options: CategoryInfo) => {
    const categoryId = options.key;

    const newCategory = getCategoryByID(categoryId);

    updateSearch(undefined, newCategory);
  };

  const clearQuery = () => updateSearch('', category);

  const showFiltersDialog = (e: React.MouseEvent): void => {
    e.preventDefault();
    onFiltersOpen();
  };

  const onAutocompleteSelect = (
    item: SearchAutocompleteOption | null | undefined
  ) => {
    if (!item) {
      clearQuery();
      return;
    }

    const query = item.filters?.find((f) => f.uri === FilterURI.Query);
    const autocompleteCategory = item.filters?.find(
      (f) => f.uri === FilterURI.Category
    );

    let newQueryValue: string | undefined = undefined;

    if (query?.values?.[0]?.label || query?.values?.[0]?.label === '') {
      newQueryValue = query.values[0].label;
    }

    let newCategory: Category | undefined = undefined;

    if (autocompleteCategory?.values?.[0]?.key) {
      newCategory = getCategoryByID(
        autocompleteCategory.values[0].key as CategoryID
      );
    }

    const newClothingType = item.filters?.find(
      (f) => f.uri === FilterURI.MarketClothingType
    )?.values?.[0]?.key;

    updateSearch(newQueryValue, newCategory, newClothingType, item.geo);
  };
  const onRecentSearchSelect = (item: RecentSearchOption) => {
    updateRecentSearch(item);
  };

  const signalDialogOpening = useCallback(() => {
    /**
     * We need to disable popping the state because otherwise,
     * when the dialog opens/closes and the location hash changes,
     * the listing page will try to reconciliate the url with the
     * redux state. The hash change happens before the location change
     * so the previous state will be re-set.
     */
    setPreventPopstate(true);
  }, [setPreventPopstate]);

  const signalDialogClosing = useCallback(() => {
    setPreventPopstate(false);
  }, [setPreventPopstate]);

  return (
    <div className={classnames(ownClasses['searchbar-container'])}>
      <div
        id="search-query-container"
        className={classnames(ownClasses['input-container'])}
      >
        <label
          htmlFor="/query/search_text"
          className={classnames('caption', ownClasses.label)}
        >
          Cosa cerchi?
        </label>

        <SearchAutocomplete
          assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL}
          hadesBasePath={process.env.NEXT_PUBLIC_HADES_BASE_URL}
          initialValue={query}
          onSelect={onAutocompleteSelect}
          onDialogOpen={signalDialogOpening}
          onDialogClose={signalDialogClosing}
          onRecentSearchSelect={onRecentSearchSelect}
        />
      </div>
      <div
        id="search-category-container"
        className={classnames(
          ownClasses['category-container'],
          ownClasses['input-container']
        )}
      >
        {!isMobile ? (
          <p className={classnames('caption', ownClasses.label)}>
            In quale categoria?
          </p>
        ) : null}

        <CategorySelector
          categoryList={categoryList}
          categoryIndex={categoryIndex}
          onCategoryChange={onCategoryChange}
          onFiltersButtonClick={showFiltersDialog}
        />
      </div>
      <div
        id="search-location-container"
        className={classnames(ownClasses['input-container'])}
      >
        {!isMobile ? (
          <label
            htmlFor="/geo"
            className={classnames('caption', ownClasses.label)}
          >
            Dove?
          </label>
        ) : null}

        <QueryGeoInput />

        {isMobile ? (
          <NationwideSearchExpander id="listing-mobile-nationwide-search-expander" />
        ) : null}
      </div>
    </div>
  );
}

SearchBar.displayName = 'SearchBar';

export default connect<StateProps, DispatchProps, unknown, RootState>(
  (state) => ({
    query: state.search.query as string,
    category: state.search.category,
  }),
  (dispatch) => ({
    updateSearch: (
      query?: string,
      category?: Category,
      clothingType?: string,
      geo?: GeoEntry
    ) => {
      batch(() => {
        if (category != null) {
          dispatch(updateCategory(category));
        }
        if (query || query === '') {
          dispatch(updateQuery(query));
        }
        dispatch(
          updateFilter({
            key: 'clt',
            value: clothingType ?? '',
            uri: FilterURI.MarketClothingType,
          })
        );
        if (geo) {
          dispatch(updateGeo(geo));
        }
      });
    },
    setPreventPopstate: (isOpen: boolean) =>
      dispatch(setPreventPopstate(isOpen)),
    updateRecentSearch: (search?: RecentSearchOption) => {
      dispatch(updateRecentSearch(search as RecentSearchOption));
    },
  })
)(SearchBar);
